/**
 * Shared rendering helpers for the project <Select> dropdowns in the exoplanet
 * transit flow (ObservationReviewDialog + TransitFilterPanel).
 *
 * Why this exists: GET /v1/projects returns every project the user can access
 * (their own + all orgs they belong to), and several may share a name — e.g.
 * multiple projects literally called "Exoplanets" owned by different
 * organizations. Rendering only `project.name` makes them indistinguishable,
 * so a user can file transit observations into the wrong org's project. The
 * API already populates `owner_id` (User or Organization doc) and `code`, so we
 * surface them here to disambiguate. Mirrors the code · name · owner pattern in
 * components/forms/observation/ProjectStep.js used by the general flow.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

// Server populates project.owner_id as either a User doc ({first_name,
// last_name, email}) or an Organization doc ({name, short_name}) — or leaves it
// as a raw id if neither resolved. Reduce to a display string.
export const ownerDisplay = (project) => {
  const o = project?.owner_id;
  if (!o || typeof o !== 'object') return '';
  const full = `${o.first_name || ''} ${o.last_name || ''}`.trim();
  return full || o.name || o.short_name || o.email || '';
};

// Two-line dropdown option: project name, then "CODE · owner" so duplicate
// names owned by different orgs can be told apart at a glance.
export const renderProjectOption = (project) => {
  const owner = ownerDisplay(project);
  const meta = [project?.code, owner].filter(Boolean).join(' · ');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Typography variant="body2" noWrap>{project?.name}</Typography>
      {meta && (
        <Typography variant="caption" color="text.secondary" noWrap>
          {meta}
        </Typography>
      )}
    </Box>
  );
};

// Compact single-line label for the Select's collapsed (selected) value, so the
// closed field stays a normal height while still naming the owner.
export const projectValueLabel = (project) => {
  if (!project) return '';
  const owner = ownerDisplay(project);
  return owner ? `${project.name} (${owner})` : project.name;
};
